import moment, { Moment } from "moment-timezone";
import * as OsDetect from "react-device-detect";
import { Layout } from "react-grid-layout";
import { isEmpty } from "lodash";
import { CvvItem } from "api/interfaces/reportInterface.interface";
import { notify } from "components/atoms/notification/Notification";
import { AddReportListType } from "pages/reports/add/AddReport";
import { RecorderType } from "components/atoms/text/labels/Type";
import { cvvEvent } from "pages/reports/view/cvv/constants";
import { DefaultTheme } from "styled-components";
import { NOTICE_COLORS } from "styles/colors";
import { DynamoDBClient } from "@aws-sdk/client-dynamodb";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import { DynamoDBDocumentClient, GetCommand } from "@aws-sdk/lib-dynamodb";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import qs from "qs";

const ua = navigator.userAgent;

/**
 * @param level 레벨 전체 이름
 * @returns string 레벨 축약어
 */
export const changeLevelToABB = ({
  level,
  parentLevel,
}: {
  level: string;
  parentLevel?: LEVEL_TYPE;
}): LEVEL_TYPE => {
  let abb;

  if (level === "Channel Partner" && parentLevel === LEVEL_TYPE.CP) {
    return LEVEL_TYPE.SP;
  }
  switch (level) {
    case "DW Admin":
      abb = LEVEL_TYPE.DW;
      break;
    case "Channel Partner":
      abb = LEVEL_TYPE.CP;
      break;
    case "End User":
      abb = LEVEL_TYPE.EU;
      break;
    default:
      return LEVEL_TYPE.INTL;
  }
  return abb;
};

export const changeLevelFromABB = (level: LEVEL_TYPE) => {
  let abb;

  switch (level) {
    case LEVEL_TYPE.DW:
      abb = "DW Admin";
      break;
    case LEVEL_TYPE.CP:
      abb = "Channel Partner";
      break;
    case LEVEL_TYPE.SP:
      abb = "Sub Channel Partner";
      break;
    case LEVEL_TYPE.EU:
      abb = "End User";
      break;
    default:
      return "";
  }
  return abb;
};

export const changeLevelFromABBToABB = ({
  level,
  parentLevel,
}: {
  level: LEVEL_TYPE;
  parentLevel?: LEVEL_TYPE;
}): LEVEL_TYPE => {
  if (level === LEVEL_TYPE.CP && parentLevel === LEVEL_TYPE.CP) {
    return LEVEL_TYPE.SP;
  }
  return level;
};

/**
 * @param str : 문자
 * @description 문자열 중 앞 글자만 대문자로 변환하는 함수
 */
export const changeFirstWordToUppercase = (str: string = "") => {
  if (str !== undefined && str !== null)
    return str.charAt(0).toUpperCase() + str.slice(1);
  else return "";
};

export function formatUppercaseOnly(input: string = ""): string {
  if (input === input.toUpperCase()) {
    return input.charAt(0).toUpperCase() + input.slice(1).toLowerCase();
  }
  return input;
}

/**
 *
 * @param amount number 숫자 단위 쉼표 표시
 * @returns number
 * @description 숫자 단위 00,000
 */
export const changeNumberToFormat = (number: number) => {
  if (number === null) {
    return;
  }
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

/**
 *
 * @param type report 페이지의 type
 * @returns type full name
 * @description ex. health -> Health, cvv -> CVV report , thumbnail -> Thumbnail Report
 */
export const changeTypeForReportType = (type: string) => {
  let fullType;

  switch (type) {
    case "HEALTH":
      fullType = "Health";
      break;
    case "INVENTORY":
      fullType = "Inventory";
      break;
    case "CVV":
      fullType = "CVV";
      break;
    case "THUMBNAIL":
      fullType = "Thumbnail";
      break;
    case "INSTALLATION":
      fullType = "Installation";
      break;
    case "TIMELAPSE":
      fullType = "Timelapse";
      break;
    default:
      return changeFirstWordToUppercase(type);
  }
  return fullType;
};

export const changeTypeNumberForReportText = (
  type: string
): AddReportListType => {
  switch (type) {
    case "HEALTH":
      return "health";
    case "CVV":
      return "cvv";
    case "THUMBNAIL":
      return "thumbnail";
    case "INVENTORY":
      return "inventory";
    case "INSTALLATION":
      return "installation";
    case "TIMELAPSE":
      return "timelapse";
    default:
      return "health";
  }
};

/**
 *
 * @param current number 현재 용량
 * @param total number 총 용량
 * @description 비율 계산
 */
export const calcStoragePercent = (
  current: number,
  total: number,
  max?: number
) => {
  if (total === 0 || total === undefined) {
    return 0;
  }
  const percent = (current / total) * 100;
  return max !== undefined ? (percent >= 100 ? 100 : percent) : percent;
};

/**
 *
 * @param current number 현재 용량
 * @param total number 총 용량
 * @returns 90% 이상이면 true, 아니면 false
 */
export const isFullStorage = (current: number, total: number) => {
  if (calcStoragePercent(current, total) >= 90) return true;
  else return false;
};

// export const commonFormat = (date: string | Date) => {
//   return `${moment(timestamp).format("MM/DD/YYYY")}  ${moment(timestamp)
//     .tz(timezone ? timezone : "UTC")
//     .format("h:mm:ss A z")}`;
// };

/**
 * @param bytes number 현재 용량
 * @param decimals 자리수
 * @returns ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
 */

// byte를 적절한 단위로 변환 후 값과 단위 따로 분리해서 결과값 도출
export function formatBytesAndUnits(
  bytes: number,
  base: number = 1024
): {
  value: number;
  unit: string;
} {
  if (bytes === 0) return { value: 0, unit: "Bytes" };

  const units = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const index = Math.min(
    Math.floor(Math.log(bytes) / Math.log(base)),
    units.length - 1
  );
  const value = parseFloat((bytes / Math.pow(base, index)).toFixed(2));

  return { value, unit: units[index] };
}

export const convertGBToBytes = (size: number, base: number = 1024) => {
  return size * Math.pow(base, 3);
};
// TB를 1024 Byte로 변환하는 함수
export const convertTBtoBytes = (tb: number, base: number = 1024): number => {
  const binaryFactor = Math.pow(base, 4); // 2^40 (1TB = 1,099,511,627,776 bytes)

  return tb * binaryFactor;
};

export const convertBytesWithUnit = (bytes: number) => {
  const { value, unit } = formatBytesAndUnits(bytes);
  return `${value} ${unit}`;
};

// byte 를 tb 로 변환하는 함수
export const convertBytesToTB = (
  bytes: number,
  binary: boolean = false
): number => {
  const decimalFactor = 1e12; // 10^12 (1TB = 1,000,000,000,000 bytes)
  const binaryFactor = Math.pow(2, 40); // 2^40 (1TB = 1,099,511,627,776 bytes)

  return bytes / (binary ? binaryFactor : decimalFactor);
};

export const formatBytesFromMB = (bytes: number, decimals = 1) => {
  if (bytes === 0) return "";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

export const formatBytes = (bytes: number, decimals = 1) => {
  if (bytes === 0) return "";

  // const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  let i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    sizes.length - 1
  );
  if (i === 0) return `${bytes} ${sizes[i]}`;
  return `${(bytes / 1024 ** i).toFixed(dm)} ${sizes[i]}`;
};

export const formatBytesToTB = (bytes: number): number => {
  return bytes / Math.pow(1024, 4);
};

export const formatBytesMemory = (bytes: number, decimals?: number) => {
  if (bytes === 0) return "";
  if (decimals === undefined) {
    decimals = 1;
  }
  // const k = 1000;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  let i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1000)),
    sizes.length - 1
  );
  if (i === 0) return `${bytes} ${sizes[i]}`;
  return `${(bytes / 1000 ** i).toFixed(dm)} ${sizes[i]}`;
};

export const getGBFromByteString = (byte: string) => {
  if (byte === undefined || !isNumber(byte)) {
    return "";
  }
  return formatBytesMemory(Number(byte), 0);
};
/**
 *
 * @param timestamp string ex)2022-10-19T09:00:00.292000Z
 * @return 19/10/2022 2:00:00 AM PDT
 *
 */
export const changeTimeStampFormat = (
  timestamp: string | Date,
  timezone?: string
) => {
  return `${moment(timestamp).format("MM/DD/YYYY")}
  \u00A0
  ${moment(timestamp)
    .tz(timezone ? timezone : "UTC")
    .format("h:mm:ss A z")}`;
};

export const changeTimeStampFormatDaySelect = (
  timestamp: string | Date,
  timezone?: string
) => {
  return moment(timestamp).format("MM/DD/YYYY");
};

/**
 *
 * @param date moment / string
 * @returns 03/11/2022 Thu
 */

export const changeDateToFormat = (date: Moment | string) => {
  return moment(date).format("MM/DD/YYYY");
};

export const changeDateToFormatMonth = (date: Moment | string) => {
  return moment(date).format("MM/YYYY");
};

export const DateFromString = (date: string) => {
  if (date !== undefined && date.length === 10) {
    const sYear = date.substring(0, 4);
    const sMonth = date.substring(5, 7);
    const sDate = date.substring(8, 10);
    return new Date(Number(sYear), Number(sMonth) - 1, Number(sDate));
  } else {
    return new Date();
  }
};

/**
 *
 * @returns 24시간을 30분 단위로 나눠서 00:00 로 변환
 */
export const createTime24for30min = () => {
  let temp = [];
  let hour = "";
  let time = "";
  let period = "";

  for (let i = 0; i < 24; i++) {
    for (let j = 0; j <= 30; j += 30) {
      // Military time 그대로 사용
      const militaryHour = i < 10 ? `0${i}` : `${i}`;
      time = j === 0 ? `0${j}` : `${j}`;

      // AM/PM 구분 설정
      if (i === 0) {
        hour = "12"; // 12 AM
        period = "AM";
      } else if (i < 12) {
        hour = i < 10 ? `0${i}` : `${i}`;
        period = "AM";
      } else if (i === 12) {
        hour = "12"; // 12 PM
        period = "PM";
      } else {
        hour = i - 12 < 10 ? `0${i - 12}` : `${i - 12}`;
        period = "PM";
      }

      temp.push({
        label: `${hour}:${time} ${period}`, // AM/PM 형식으로 표시
        value: `${militaryHour}:${time}`, // military 시간 유지
      });
    }
  }
  return temp;
};

/**
 *
 * @param date1 string 이나 moment 형식
 * @param date2 string 이나 moment 형식
 * @returns  절대값으로 받아옴
 */
export const substractTime = (
  date1: string | moment.Moment,
  date2: string | moment.Moment
) => {
  let returnSubstractTimeString = "";
  let formatDate1 = moment(date1);
  let formatDate2 = moment(date2);

  // console.log(formatDate1 + "/" + formatDate2);
  var diffTime = {
    month: moment.duration(formatDate2.diff(formatDate1)).months(),
    day: moment.duration(formatDate2.diff(formatDate1)).days(),
    hour: moment.duration(formatDate2.diff(formatDate1)).hours(),
    minute: moment.duration(formatDate2.diff(formatDate1)).minutes(),
  };
  // console.log(diffTime);
  if (diffTime.month !== 0) {
    return returnSubstractTimeString.concat(
      String(Math.abs(diffTime.month)),
      diffTime.month === 1 ? " Month" : " Months"
    );
  }
  if (diffTime.day !== 0) {
    return returnSubstractTimeString.concat(
      String(Math.abs(diffTime.day)),
      diffTime.day === 1 ? " Day" : " Days"
    );
  }
  if (diffTime.hour !== 0) {
    return returnSubstractTimeString.concat(
      String(Math.abs(diffTime.hour)),
      diffTime.hour === 1 ? " Hour" : " Hours"
    );
  }
  if (diffTime.minute !== 0) {
    return returnSubstractTimeString.concat(
      String(Math.abs(diffTime.minute)),
      diffTime.minute === 1 ? " Minute" : " Minutes"
    );
  }
  if (diffTime.minute === 0) {
    return returnSubstractTimeString.concat("1 Minute");
  }
  return returnSubstractTimeString;
};

/**
 *
 * @param string string
 * @returns ex. #ff1234
 */
export const stringToColor = (string: string) => {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
};

/**
 *
 * @param name ex. username
 * @returns user 프로필 축약어 ex. Arm Strong -> AS
 */
export const stringAvatar = (name: string) => {
  if (name !== undefined && !checkEmpty(name)) {
    const removeWhite = name.replace(/^\s+|\s+$/gm, "");
    const nameCount = removeWhite.split(" ").length;

    const splittedName = removeWhite.split(" ")[0];

    if (nameCount === 1) {
      if (splittedName.length >= 2) {
        return `${(splittedName[0] || "").toUpperCase()}${(
          removeWhite.split(" ")[0][1] || ""
        ).toUpperCase()}`;
      } else {
        return `${(splittedName[0] || "").toUpperCase()}`;
      }
    } else {
      return `${(splittedName[0] || "").toUpperCase()}${(
        removeWhite.split(" ")[nameCount - 1][0] || ""
      ).toUpperCase()}`;
    }
  } else {
    return "NA";
  }
};

export const calRowCount = () => {
  var maxWidth = window.innerWidth;
  // console.log("availWidth=" + maxWidth);
  let count = 0;
  if (maxWidth >= 3800) {
    count = 25;
  } else if (maxWidth > 2560) {
    count = 18;
  } else if (maxWidth > 1920) {
    count = 15;
  } else {
    count = 10;
  }
  return count;
};

export const getBrowserInfo = () => {
  return (
    navigator.userAgent.match(/Chrome|Firefox|Safari|Edge/)?.[0] || "Unknown"
  );
};
export const getClientDevice = () => {
  if (OsDetect.isDesktop) {
    if (OsDetect.isWindows) {
      return "Window PC";
    } else if (OsDetect.isMacOs) {
      return "MAC PC";
    } else if (ua.match(/Linux/)) {
      return "Linux PC";
    } else {
      return "PC";
    }
  } else if (OsDetect.isTablet) {
    if (OsDetect.isIOS) {
      return "IPad";
    } else if (OsDetect.isWindows) {
      return "Window Tablet";
    } else if (OsDetect.isAndroid) {
      return "Android Tablet";
    } else {
      return "Tablet";
    }
  } else if (OsDetect.isMobile) {
    if (OsDetect.isAndroid) {
      return "Android Phone";
    } else if (OsDetect.isIOS) {
      return "IPhone";
    } else if (OsDetect.isMIUI) {
      return "MIUI Phone";
    } else {
      return "Phone";
    }
  } else if (OsDetect.isSmartTV) {
    return "Smart TV";
  } else {
    return "ETC";
  }
};

export const changeStatusString = (status: string) => {
  switch (status) {
    case "CLOSED":
      return "Closed";
    case "ASSIGNED":
      return "Assigned";
    case "ACTIVE":
      return "Active";
    default:
      return status;
  }
};

type ConvertTypeParam = {
  type: string;
  brand?: string;
  displayType?: string;
};

export const convertType = ({
  type,
  brand,
  displayType,
}: ConvertTypeParam): RecorderType => {
  // console.log(type, brand, displayType);
  if (displayType) {
    return displayType.toUpperCase() as RecorderType;
  }
  if (type === undefined) {
    return "UNKNOWN";
  }

  switch (type.toUpperCase()) {
    case "NVR":
      return brand?.toUpperCase() === "TVT" ? "COVA" : "VMAX-IP";
    case "DVR":
      return "VMAX-A1";
    case "SPECTRUM":
      return "SPECTRUM";
    case "COVA":
      return "COVA";
    default:
      return "UNKNOWN";
  }
};

export const convertType2 = (type: string, brand?: string): RecorderType => {
  if (type === undefined) {
    return "UNKNOWN";
  }

  switch (type.toUpperCase()) {
    case "NVR":
      return brand?.toUpperCase() === "TVT" ? "COVA" : "VMAX-IP";
    case "DVR":
      return "VMAX-A1";
    case "SPECTRUM":
      return "SPECTRUM";
    case "COVA":
      return "COVA";
    default:
      return "UNKNOWN";
  }
};

export const changeLayoutToRes = (layouts: Layout[]) => {
  return layouts.map((layout) => {
    return {
      ...layout,
      h: layout.h + 1,
    };
  });
};

//Recorder list 에서 하위 system의 alarm 상태를 check하는 function
export const checkStatus = (totalCount: number, onlineCount: number) => {
  if (totalCount === 1) {
    if (totalCount === onlineCount) {
      return "online";
    } else {
      return "offline";
    }
  } else {
    if (onlineCount === 0) {
      return "offline";
    } else if (totalCount === onlineCount) {
      return "online";
    } else {
      return "warning";
    }
  }
};

export const getDuplicateCount = (arr: any[]) => {
  let result: any = {};
  for (const el of arr) {
    result[el] = (result[el] || 0) + 1;
  }
  return result;
};

export const filterNonNull = (obj: any) => {
  return Object.fromEntries(Object.entries(obj).filter(([k, v]) => v));
};

/**
 * 배열 사람명수 외 표시
 * ex) steven armtsrong + 2
 */
export const showCount = (arr: string[]) => {
  if (arr.length === 0) {
    return "Tampered";
  } else if (arr.length > 2) {
    return `${arr.filter((a) => a)[0].concat(", ", arr[1])} + ${
      arr.length - 2
    }`;
  } else return arr.join(", ");
};

/**
 * html 태그 없애기
 */
export const deleteHtmlTag = (string: string) => {
  const reg = /<[^>]*>?/g;
  return string !== undefined ? string.replace(reg, "") : "";
};

export const openInNewTab = (url: string) => {
  window.open(url, "_blank", "noreferrer");
};

/**
 * tree array -> flatten array
 * @param destArray
 * @param nodeList
 */
export const flatten = (destArray: any[], nodeList: any[]) => {
  nodeList.forEach((node) => {
    destArray.push(node);
    flatten(destArray, node.subRows || []);
  });
};

export const flattenArray = (nodeList: any[]) => {
  let temp: any[] = [];
  flatten(temp, nodeList);
  return temp;
};

const renameKeys = (obj: any, newKeys: any) => {
  const keyValues = Object.keys(obj).map((key) => {
    const newKey = newKeys[key] || key;
    return { [newKey]: obj[key] };
  });
  return Object.assign({}, ...keyValues);
};
export const renameObjectKeys = (obj: any, newKeys: any) => {
  const renamedObj = renameKeys(obj, newKeys);

  if (obj.subRows) {
    renamedObj.children = obj.subRows.map((subRow: any) =>
      renameObjectKeys(subRow, newKeys)
    );
  }
  return renamedObj;
};

export const renameArrayKeys = (array: any[], newKeys: any) => {
  return array.map((arr) => renameObjectKeys(arr, newKeys));
};

export const extractFileName = (path: string) => {
  let fileName = path.split("/").pop();
  if (fileName !== undefined) {
    fileName = fileName.split(".")[0];
  }
  return fileName;
};

export const getStartPageUrl = (page: number) => {
  let startUrl = "/dashboard/map";
  switch (page) {
    case 1:
      startUrl = "/dashboard/map";
      break;
    case 2:
      startUrl = "/dashboard/chart";
      break;
    case 3:
      startUrl = "/dashboard/admin";
      break;
    case 4:
      startUrl = "/account";
      break;
    case 5:
      startUrl = "/recorders/list";
      break;
    default:
      startUrl = "/dashboard/map";
      break;
  }
  return startUrl;
};

export const getDomainUrl = (url: string) => {
  if (url === undefined || checkEmpty(url)) {
    return "";
  } else if (url.includes("onvif/device_service")) {
    const urlObject = new URL(url);
    return urlObject.origin;
  } else {
    return url;
  }
};

export const isNumber = (s: any) => {
  s += ""; // 문자열로 변환
  s = s.replace(/^\s*|\s*$/g, ""); // 좌우 공백 제거
  if (s === "" || isNaN(s)) return false;
  return true;
};

export const checkCVVError = (item: CvvItem) => {
  return cvvEvent.some((event) => {
    return item[event] === true;
  });
};

export const checkCVVErrorBlocking = (item: CvvItem) => {
  return item.blur || item.dust || item.glare || item.spider;
};

export const handleCopyClipBoard = (
  text: string,
  copyText: string = "Organization ID"
) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      notify("success", `${copyText} copied successfully.`);
    })
    .catch(() => {
      notify("error", `${copyText} copy failed.`);
    });
};

export const getIDfromYoutubeUrl = (url: string) => {
  const regExp =
    /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);

  if (match && match[2].length === 11) {
    return match[2];
  }

  return "";
};

//YouTube URL에서 재생목록이나 추가 파라미터를 제외하고 기본 URL만 추출
export const extractYouTubeBaseUrl = (url: string): string => {
  const urlObj = new URL(url);
  const videoId = urlObj.searchParams.get("v");

  // 'v' 파라미터를 제외한 나머지 삭제
  urlObj.searchParams.forEach((_, key) => {
    if (key !== "v") {
      urlObj.searchParams.delete(key);
    }
  });

  // console.log(url, urlObj, videoId, urlObj.toString());
  // v 파라미터가 있는 경우 기본 URL 반환
  if (videoId) {
    return `https://www.youtube.com/watch?v=${videoId}`;
  }

  // v 파라미터가 없는 경우 수정된 URL 반환
  return urlObj.toString();
};

export const matchHashTag = (selectTags: string[], compareTag: string) => {
  if (selectTags === undefined || compareTag === undefined) {
    return false;
  } else {
    const match = selectTags.find(
      (selectTag) => selectTag.toLowerCase() === compareTag.toLowerCase()
    );
    if (match) {
      return true;
    } else {
      return false;
    }
  }
};
function getExtensionFromMimeType(mime: string): string {
  const map: Record<string, string> = {
    // 📄 문서
    "application/pdf": "pdf",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "docx",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "application/vnd.ms-powerpoint": "ppt",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      "pptx",
    "text/plain": "txt",
    "text/csv": "csv",
    "application/json": "json",
    "application/xml": "xml",

    // 🖼️ 이미지
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/gif": "gif",
    "image/svg+xml": "svg",
    "image/webp": "webp",
    "image/bmp": "bmp",
    "image/tiff": "tiff",

    // 🎵 오디오
    "audio/mpeg": "mp3",
    "audio/wav": "wav",
    "audio/ogg": "ogg",
    "audio/webm": "webm",
    "audio/aac": "aac",

    // 🎬 비디오
    "video/mp4": "mp4",
    "video/x-msvideo": "avi",
    "video/x-matroska": "mkv",
    "video/webm": "webm",
    "video/ogg": "ogv",
    "video/quicktime": "mov",

    // 🗜️ 압축
    "application/zip": "zip",
    "application/x-rar-compressed": "rar",
    "application/x-7z-compressed": "7z",
    "application/gzip": "gz",
    "application/x-tar": "tar",
    "application/x-compressed": "zip",
    "application/x-apple-diskimage": "dmg",
    "application/x-apple-diskimage-2disk": "dmg",
    "application/x-apple-diskimage-2disk-installer": "dmg",
    "application/x-apple-diskimage-3disk": "dmg",
    "application/x-apple-diskimage-3disk-installer": "dmg",
    "application/x-apple-diskimage-4disk": "dmg",
    "application/x-apple-diskimage-4disk-installer": "dmg",
  };

  return map[mime] ?? "bin"; // 기본값은 'bin'
}
export const downloadFromUrl = async (url: string, fileName?: string) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      notify("error", "Failed to download file");
      return;
    }
    const file = await response.blob();
    if (!fileName) {
      fileName = url.split("/").pop();
    }
    const extension = getExtensionFromMimeType(file.type);
    if (!extension) {
      notify("error", "Failed to download file");
      return;
    }
    fileName =
      fileName && fileName.includes(".")
        ? fileName
        : (fileName
            ? fileName.slice(0, fileName.lastIndexOf("."))
            : `unknown_${Date.now()}`) + `.${extension}`;
    const downloadUrl = window.URL.createObjectURL(file);

    const anchorElement = document.createElement("a");
    document.body.appendChild(anchorElement);
    if (fileName !== undefined) {
      anchorElement.download = fileName;
    }
    anchorElement.href = downloadUrl;
    anchorElement.click();

    document.body.removeChild(anchorElement);
  } catch (error) {
    notify("error", "Failed to download file");
  }
};

// url 을 file 형식으로 변환
export const convertURLtoFile = async (url: string) => {
  const response = await fetch(url);
  const data = await response.blob();
  const ext = url.split(".").pop(); // url 구조에 맞게 수정할 것
  const filename = url.split("/").pop(); // url 구조에 맞게 수정할 것
  const metadata = { type: `image/${ext}` };
  return new File([data], filename!, metadata);
};

export const openNewWindow = (targetUrl: string) => {
  var linkElement = document.createElement("a");
  linkElement.href = targetUrl;
  linkElement.target = "_blank";
  linkElement.rel = "noopener noreferrer";
  window.document.body.appendChild(linkElement);
  linkElement.setAttribute("href", targetUrl);
  linkElement.click();
  window.document.body.removeChild(linkElement);
};

/**
 * 대소문자 구별없이 검색 true / false 로 변환
 * @param string
 * @param searchInput
 * @returns string 이 searchInput 에 포함될경우 : true / 불포함일경우 :false
 */
export const matchStringSearchKeyword = (
  string: string,
  searchInput: string
) => {
  if (typeof string !== "string") {
    console.error("Invalid string input:", string);
    return false;
  }

  // RegExp 특수문자 escape 처리
  const escapedInput = searchInput.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const regex = new RegExp(escapedInput, "i");
  return regex.test(string);
};

var blank_pattern = /^\s+|\s+$/g;

/**
 *
 * @param str keyword
 * @returns boolean
 * @description 공백만 인지 아닌지 확인
 */
export const checkBlankSpace = (str: string) => {
  return str.replace(blank_pattern, "") === "";
};

/**
 *
 * @param str keyword
 * @returns boolean
 * @description 아무것도 없을때는 검색 가능하지만 공백만 있을때는 검색 불가능
 */
export const checkKeywordSpace = (str: string) => {
  // console.log(str !== "", checkBlankSpace(str));
  return str !== "" && checkBlankSpace(str);
};

export const getChartColorSchema = () => {
  const category10 =
    "1f77b4ff7f0e2ca02cd627289467bd8c564be377c27f7f7fbcbd2217becf";
  var n = (category10.length / 6) | 0,
    colors = new Array(n),
    i = 0;
  while (i < n) colors[i] = "#" + category10.slice(i * 6, ++i * 6);
  return colors;
};

export const maskInternationalPhoneNumber = (phoneNumber: string): string => {
  // 정규식을 사용하여 가운데 4~6번째 자리를 *로 대체
  return phoneNumber.replace(/(\+\d{1,3})(\d{3})(\d{3})(\d{4})/, "$1$2***$4");
};

export const isVideoFile = (fileName: string): boolean => {
  const acceptableVideos = [".webm", ".mp4"];
  return acceptableVideos.some((ext) => {
    return fileName.endsWith(ext);
  });
};

export const isImageFile = (fileName: string): boolean => {
  const acceptableVideos = [".png", ".jpeg", "jpg", "bmp", "gif", "webp"];
  return acceptableVideos.some((ext) => {
    return fileName.endsWith(ext);
  });
};

export const findPopup = (key: string) => {
  return document.querySelector(`[data-key="${key}"]`);
};

export const splitFullName = (name: string, isLastName?: true) => {
  if (!name.trim()) return ""; // 빈 문자열 처리

  const nameParts = name.split(" ");
  if (nameParts.length === 1) return name; // 공백 없는 경우

  const lastName = nameParts.pop()!; // 마지막 단어를 lastName으로 설정
  const firstName = nameParts.join(" "); // 나머지 단어들을 firstName으로 설정

  return isLastName ? lastName : firstName;
};

/**
 * 이미지 경로의 특수문자를 URL 인코딩하여 반환합니다.
 * @param path 이미지 경로
 * @returns URL 인코딩된 이미지 경로
 */
export const encodeImagePath = (path: string): string => {
  if (!path) return "";

  try {
    const url = new URL(path);
    const segments = url.pathname.split("/");
    const encodedSegments = segments.map((segment) => {
      try {
        decodeURIComponent(segment);
        return segment;
      } catch (e) {
        return encodeURIComponent(segment);
      }
    });

    url.pathname = encodedSegments.join("/");
    return url.toString();
  } catch (e) {
    // URL 파싱에 실패한 경우 그대로 리턴
    return path;
  }
};

export const extractTextFromHTML = (html: string): string => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  const text = tempDiv.textContent || tempDiv.innerText || "";
  return toTrim(text);
};
const parseVersion = (version: string): number[] => {
  // 예: '1.0.1-2' -> [1, 0, 1, 2]
  const [main, pre] = version.split("-");
  const parts = main.split(".").map(Number);
  if (pre !== undefined) {
    parts.push(Number(pre));
  }
  return parts;
};

const compareVersions = (
  installedVersion: string,
  latestVersion: string
): number => {
  const a = parseVersion(installedVersion);
  const b = parseVersion(latestVersion);
  const maxLength = Math.max(a.length, b.length);

  for (let i = 0; i < maxLength; i++) {
    const numA = a[i] ?? 0;
    const numB = b[i] ?? 0;
    if (numA > numB) return 1;
    if (numA < numB) return -1;
  }
  return 0; // equal
};

export const isVersionGreater = ({
  installedVersion,
  latestVersion,
}: {
  installedVersion: string;
  latestVersion: string;
}): boolean => {
  return compareVersions(installedVersion, latestVersion) >= 0;
};

export const getRecorderColor = ({
  theme,
  type,
}: {
  theme: DefaultTheme;
  type: RecorderType;
}) => {
  if (type.includes("NVR") || type.includes("VMAX")) {
    return theme.COLORS.PRIMARY1;
  }
  if (type.toUpperCase() === "SPECTRUM") {
    return "#175B22";
  }
  if (type.toUpperCase() === "COVA") {
    return NOTICE_COLORS.PURPLE;
  }
  return `${theme.COLORS.WHITE}40`;
};

export const formatNumber = (
  number: number = 0,
  options?: { fractionDigits?: number; truncate?: boolean }
) => {
  if (isNaN(number)) return 0;

  // Backwards compatible default: keep the old `toLocaleString` behavior.
  if (!options) return number.toLocaleString("en-US");

  return formatNumberFixed(number, options.fractionDigits ?? 0, {
    locale: "en-US",
    truncate: options.truncate ?? false,
  });
};

export const truncateToDigits = (value: number, digits: number): number => {
  const safeValue = Number.isFinite(value) ? value : 0;
  const safeDigits = Number.isFinite(digits)
    ? Math.max(0, Math.floor(digits))
    : 0;
  const pow = Math.pow(10, safeDigits);
  // "버림": 0 방향으로 절사 (음수도 -1.29 -> -1.2)
  return Math.trunc(safeValue * pow) / pow;
};

export const formatNumberFixed = (
  value: number,
  fractionDigits: number,
  {
    locale = "en-US",
    truncate = true,
  }: { locale?: string; truncate?: boolean } = {}
): string => {
  const safeDigits = Number.isFinite(fractionDigits)
    ? Math.max(0, Math.floor(fractionDigits))
    : 0;
  const safeValue = Number.isFinite(value) ? value : 0;
  const normalized = truncate
    ? truncateToDigits(safeValue, safeDigits)
    : safeValue;

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: safeDigits,
    maximumFractionDigits: safeDigits,
  }).format(normalized);
};

//문자열을 Uint8Array로 변환 (Base64 URL 디코딩)
export const encodeArrayBuffer = (string: string) => {
  try {
    // Base64 URL을 일반 Base64로 변환
    const base64 = string.replace(/-/g, "+").replace(/_/g, "/");

    // 패딩 추가 - while 루프를 사용하여 더 안전하게 처리
    let padded = base64;
    while (padded.length % 4) {
      padded += "=";
    }

    // Base64 유효성 검사
    const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
    if (!base64Regex.test(padded)) {
      throw new Error(`Invalid Base64 string: ${padded}`);
    }

    // atob로 디코딩
    const binaryString = atob(padded);
    return Uint8Array.from(binaryString, (c) => c.charCodeAt(0));
  } catch (error: any) {
    console.error("encodeArrayBuffer error:", error);
    console.error("Input string:", string);
    throw new Error(`Failed to decode Base64 URL string: ${error?.message}`);
  }
};

//일반 문자열을 Uint8Array로 변환 (UTF-8 인코딩)
export const encodeStringToUint8Array = (string: string) => {
  try {
    // TextEncoder를 사용하여 UTF-8로 인코딩
    const encoder = new TextEncoder();
    return encoder.encode(string);
  } catch (error) {
    console.error("encodeStringToUint8Array error:", error);
    console.error("Input string:", string);
    throw new Error(
      `Failed to encode string to Uint8Array: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }
};

//Base64 URL → 일반 Base64 → 바이너리 문자열 → Uint8Array
export const decodeBase64Url = (base64Url: string) => {
  // Base64 URL을 일반 Base64로 변환
  let base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  // 패딩 추가
  while (base64.length % 4) {
    base64 += "=";
  }
  // Base64 디코딩 후 Uint8Array로 변환
  const binaryString = atob(base64);
  return Uint8Array.from(binaryString, (c) => c.charCodeAt(0));
};

export const convertBase64ToBase64Url = (base64: string) => {
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

//ArrayBuffer를 Base64 URL로 변환
export const encodeArrayBufferToString = (buffer: ArrayBuffer): string => {
  // ArrayBuffer를 일반 Base64로 인코딩한 후 Base64 URL로 변환
  // ArrayBuffer를 Uint8Array로 변환
  const bytes = new Uint8Array(buffer);
  // 각 바이트를 문자로 변환 후 base64 인코딩
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  //일반 Base64를 Base64 URL로 변환
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

//string을 ArrayBuffer로 변환
export const decordeStringToArrayBuffer = (string: string): ArrayBuffer => {
  // Base64URL을 일반 Base64로 변환
  const atboString = string
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(Math.ceil(string.length / 4) * 4, "=");

  // Base64 → binary string → Uint8Array
  const raw = atob(atboString);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    arr[i] = raw.charCodeAt(i);
  }
  return arr.buffer;
};

export const createUuid = (string: string) => {
  return string + "-" + Math.random().toString(36).substring(2, 15);
};

export const numToPadStart = (number: number, length: number) => {
  return number.toString().padStart(length, "0");
};

export function getIOSVersion(): string | null {
  // iPhone; CPU iPhone OS 17_6_1 like Mac OS X
  const match = ua.match(/OS (\d+)_?(\d+)?_?(\d+)?/);
  if (!match) return null;

  const major = match[1];
  // const minor = match[2] || "0";
  // const patch = match[3] || "0";
  return major;
}

export function getAndroidVersion(): string | null {
  const match = ua.match(/Android (\d+)/);
  if (match) return match[1];
  return null;
}

type BrowserInfo = {
  name: string;
  version: string;
};

export function getBrowserVersion(): BrowserInfo {
  let browser = {
    name: "Other",
    version: "",
  };
  if (/SamsungBrowser/i.test(ua)) {
    browser.name = "Samsung Internet";
    const match = ua.match(/SamsungBrowser\/([\d.]+)/);
    browser.version = match ? match[1] : "";
  } else if (/OPR|Opera/i.test(ua)) {
    browser.name = "Opera";
    const match = ua.match(/(Opera|OPR)\/([\d.]+)/);
    browser.version = match ? match[2] : "";
  } else if (/Edg/i.test(ua)) {
    browser.name = "Edge";
    const match = ua.match(/Edg\/([\d.]+)/);
    browser.version = match ? match[1] : "";
  } else if (
    (/Chrome/i.test(ua) || /CriOS/i.test(ua)) &&
    !/Edg|OPR|SamsungBrowser/i.test(ua)
  ) {
    browser.name = "Chrome";
    const match = ua.match(/Chrome\/([\d.]+)/);
    browser.version = match ? match[1] : "";
  } else if (/Safari/i.test(ua) && !/Chrome|CriOS|Chromium/i.test(ua)) {
    browser.name = "Safari";
    const match = ua.match(/Version\/([\d.]+)/);
    browser.version = match ? match[1] : "";
  } else if (/Firefox/i.test(ua)) {
    browser.name = "Firefox";
    const match = ua.match(/Firefox\/([\d.]+)/);
    browser.version = match ? match[1] : "";
  }
  return browser;
}

type BrowserName = "Safari" | "Chrome" | "Firefox" | "Edge" | "Other";

export function toNumber(ver: string | null): number | null {
  if (!ver) return null;
  // "17.6.1" -> 17 만 비교하고 싶으면 split해서 앞에 것만
  const major = ver.split(".")[0];
  const n = Number(major);
  return isNaN(n) ? null : n;
}

export function isBrowserSupported(
  browser: { name: string; version: string | null },
  requirements: Partial<Record<BrowserName, number>>
): boolean {
  const bname = (browser.name || "Other") as BrowserName;
  const need = requirements[bname];
  if (!need) return true; // 이 브라우저는 최소버전 요구 없음
  const cur = toNumber(browser.version);
  if (cur == null) return false;
  return cur >= need;
}

export function isOSSupported(os: string, version: number): boolean {
  if (!os) return false;
  if (!isNaN(Number(os)) && Number(os) < version) {
    return false;
  }
  return true;
}

export async function checkPasskeySupport(): Promise<boolean> {
  const isUserVerified =
    await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  if (!isUserVerified) {
    return false;
  }
  const browser = getBrowserVersion();
  return isBrowserSupported(browser, {
    Chrome: 110,
    Firefox: 122,
    Safari: 16,
    Edge: 110,
    Other: 110,
  });
}

export const checkMaintenance = async (): Promise<any> => {
  const tableName = process.env.REACT_APP_GROUPS_TABLE_NAME || "";
  const key = { id: "maintenance" };
  const client = new DynamoDBClient({
    region: process.env.REACT_APP_CREDENTAL_REGION || "",
    credentials: fromCognitoIdentityPool({
      clientConfig: { region: process.env.REACT_APP_CREDENTAL_REGION || "" },
      identityPoolId: process.env.REACT_APP_CREDENTAL_ID || "",
    }),
  });
  const dynamoDocClient = DynamoDBDocumentClient.from(client);
  const getCommand = new GetCommand({
    TableName: tableName,
    Key: key,
    ConsistentRead: true,
  });
  return dynamoDocClient.send(getCommand).then((res) => {
    return res.Item;
  });
};

export const toTrim = (value: any): string => {
  if (typeof value === "string") {
    return (value ?? "").trim();
  }
  return "";
};
export const checkEmpty = (value: any) => {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") {
    return isEmpty(toTrim(value));
  }
  return isEmpty(value);
};
export const toTrimObject = (obj: any) => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      if (typeof value === "string") {
        return [key, toTrim(value)];
      }
      return [key, value];
    })
  );
};

export const getSearchEmail = (location: {
  search: string;
  state?: { search?: string; email?: string } | null;
}) => {
  const searchParams = qs.parse(
    location.state?.search ? location.state.search : location.search,
    {
      ignoreQueryPrefix: true,
      decoder: (s) => decodeURIComponent(s),
    }
  );
  const email = searchParams?.email;
  return email ? toTrim(email) : "";
};

export const getRememberedEmail = () => {
  if (typeof window === "undefined") return "";
  try {
    const remember = window.localStorage.getItem("remember");
    if (!remember || !JSON.parse(remember)) return "";
    const storedEmail = window.localStorage.getItem("email");
    return storedEmail ? JSON.parse(storedEmail) : "";
  } catch {
    return "";
  }
};

export const getInitialLoginEmail = (location: {
  search: string;
  state?: { search?: string; email?: string } | null;
}) => {
  const email = getSearchEmail(location);
  return email ? email : getRememberedEmail();
};
